import { twMerge } from 'tailwind-merge';

export type MemberTab = 'plays' | 'futures';

/**
 * The controls row above the play sections (#2004): "Hide props & futures" on the left,
 * then the member's Plays/Futures switch on the right.
 *
 * Plays/Futures is a segmented control because it's a view switch between two peers —
 * the same shape as the tab strip it replaces, now sitting in line with the other
 * controls.
 *
 * There is no sort control. It offered "Game time" and "Release time", and the two
 * produced the same list: the pending section was never wired to it (it is always
 * kickoff order), and on the graded list the two keys barely differ, because a pick is
 * released within hours of its own kickoff and in much the same sequence. A control
 * whose options are indistinguishable is worse than no control, so each section now
 * carries the one order that suits it — pending by next kickoff, graded newest first —
 * and the release time reads off each row instead (see PlayRow).
 */
export function ResultsControls({
  hideProps,
  onHideProps,
  memberTab,
  onMemberTab,
  showMemberTabs = false,
}: {
  hideProps: boolean;
  onHideProps: (next: boolean) => void;
  memberTab?: MemberTab;
  onMemberTab?: (next: MemberTab) => void;
  showMemberTabs?: boolean;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {showMemberTabs && memberTab && onMemberTab && (
          <div className="inline-flex h-8 shrink-0 items-center rounded-lg bg-zinc-100 p-0.5 dark:bg-white/5">
            {(
              [
                ['plays', 'Plays'],
                ['futures', 'Futures'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => onMemberTab(value)}
                aria-pressed={memberTab === value}
                className={twMerge(
                  'inline-flex h-full items-center rounded-md px-3 font-semibold text-xs transition',
                  memberTab === value
                    ? 'bg-white text-zinc-900 shadow-sm dark:bg-white/15 dark:text-white'
                    : 'text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-white',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => onHideProps(!hideProps)}
        aria-pressed={hideProps}
        className="flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-300"
      >
        <span
          className={twMerge(
            'relative h-5 w-9 shrink-0 rounded-full transition',
            hideProps ? 'bg-emerald-600' : 'bg-zinc-300 dark:bg-white/15',
          )}
        >
          <span
            className={twMerge(
              'absolute top-0.5 left-0.5 size-4 rounded-full bg-white shadow-sm transition',
              hideProps && 'translate-x-4',
            )}
          />
        </span>
        Hide props &amp; futures
      </button>
    </div>
  );
}
